import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User } from '../types';
import { useAccountFilter } from '../contexts/AccountFilterContext';
import useTrialStatus from '../hooks/useTrialStatus';

interface TestNavigationProps {
  onLogout: () => void;
  user: User;
}

const TestNavigation: React.FC<TestNavigationProps> = ({ onLogout, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accountFilter, setAccountFilter } = useAccountFilter();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { daysRemaining, trialExpired, showCountdown } = useTrialStatus();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Disabled scroll behavior - keep navigation constant size
  // useEffect(() => {
  //   const handleScroll = () => {
  //     const scrollTop = window.scrollY;
  //     setIsScrolled(scrollTop > 20);
  //   };

  //   window.addEventListener('scroll', handleScroll, { passive: true });
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, []);

  // Handle click outside to close user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 overflow-visible transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-3'
      }`}
      style={{
        backgroundColor: 'var(--background-card)',
        borderBottom: '1px solid var(--border-color)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div className="max-w-7xl mx-auto overflow-visible px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <span className="text-lg">💰</span>
              <span className={`font-bold ${isScrolled ? 'text-lg' : 'text-xl'} transition-all duration-300`}>
                Wealthini
              </span>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/') ? 'text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive('/') ? 'var(--primary-color)' : 'transparent',
                color: isActive('/') ? 'white' : 'var(--text-secondary)'
              }}
            >
              📊 Dashboard
            </Link>
            <Link
              to="/assets"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/assets') ? 'text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive('/assets') ? 'var(--primary-color)' : 'transparent',
                color: isActive('/assets') ? 'white' : 'var(--text-secondary)'
              }}
            >
              💰 Assets
            </Link>
            <Link
              to="/liabilities"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/liabilities') ? 'text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive('/liabilities') ? 'var(--primary-color)' : 'transparent',
                color: isActive('/liabilities') ? 'white' : 'var(--text-secondary)'
              }}
            >
              💳 Liabilities
            </Link>
            <Link
              to="/cash-flow"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/cash-flow') ? 'text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive('/cash-flow') ? 'var(--primary-color)' : 'transparent',
                color: isActive('/cash-flow') ? 'white' : 'var(--text-secondary)'
              }}
            >
              📈 Cash Flow
            </Link>
            <Link
              to="/transactions"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/transactions') ? 'text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive('/transactions') ? 'var(--primary-color)' : 'transparent',
                color: isActive('/transactions') ? 'white' : 'var(--text-secondary)'
              }}
            >
              💳 Transactions
            </Link>
          </div>

          {/* Right Side Controls */}
          <div className="flex min-w-0 items-center space-x-3 overflow-visible">
            {/* Account Filter */}
            <div className="hidden sm:flex items-center">
              <div className="flex rounded-md shadow-sm">
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-l-md border transition-all duration-200 ${
                    accountFilter === 'all' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: accountFilter === 'all' ? 'var(--primary-color)' : 'transparent',
                    color: accountFilter === 'all' ? 'white' : 'var(--text-secondary)',
                    borderColor: accountFilter === 'all' ? 'var(--primary-color)' : 'var(--border-color)'
                  }}
                  onClick={() => setAccountFilter('all')}
                >
                  All
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium border-t border-r border-b transition-all duration-200 ${
                    accountFilter === 'personal' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: accountFilter === 'personal' ? 'var(--primary-color)' : 'transparent',
                    color: accountFilter === 'personal' ? 'white' : 'var(--text-secondary)',
                    borderColor: accountFilter === 'personal' ? 'var(--primary-color)' : 'var(--border-color)'
                  }}
                  onClick={() => setAccountFilter('personal')}
                >
                  Personal
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-r-md border-t border-r border-b transition-all duration-200 ${
                    accountFilter === 'business' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: accountFilter === 'business' ? 'var(--primary-color)' : 'transparent',
                    color: accountFilter === 'business' ? 'white' : 'var(--text-secondary)',
                    borderColor: accountFilter === 'business' ? 'var(--primary-color)' : 'var(--border-color)'
                  }}
                  onClick={() => setAccountFilter('business')}
                >
                  Business
                </button>
              </div>
            </div>

            {/* User menu: anchor dropdown to the profile control only (not the trial pill + row) */}
            <div className="flex items-center gap-2 shrink-0">
              {showCountdown && (
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors border border-purple-500/30 whitespace-nowrap"
                >
                  {trialExpired ? 'Trial ended · Upgrade' : `${daysRemaining}d left · Upgrade`}
                </button>
              )}
              <div className="user-dropdown relative">
                <button
                  type="button"
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                    style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                  >
                    {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                  </div>
                  <span className="hidden sm:block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {user?.first_name || user?.email}
                  </span>
                  <span className="text-xs">▼</span>
                </button>

                {showUserDropdown && (
                  <div
                    className="absolute right-0 top-full z-[100] mt-2 w-56 rounded-lg border shadow-lg"
                    style={{
                      backgroundColor: 'var(--background-card)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                  <div className="py-2">
                    <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {user?.first_name || 'User'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {user?.email}
                      </p>
                    </div>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm hover:bg-gray-700 hover:text-white transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => setShowUserDropdown(false)}
                    >
                      👤 Profile Settings
                    </Link>
                    <Link
                      to="/banks"
                      className="block px-4 py-2 text-sm hover:bg-gray-700 hover:text-white transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => setShowUserDropdown(false)}
                    >
                      🏦 Bank Connections
                    </Link>
                    <Link
                      to="/categorization"
                      className="block px-4 py-2 text-sm hover:bg-gray-700 hover:text-white transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => setShowUserDropdown(false)}
                    >
                      🏷️ Categories
                    </Link>
                    <Link
                      to="/business-accounting"
                      className="block px-4 py-2 text-sm hover:bg-gray-700 hover:text-white transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => setShowUserDropdown(false)}
                    >
                      💼 Business Accounting
                    </Link>
                    <Link
                      to="/import"
                      className="block px-4 py-2 text-sm hover:bg-gray-700 hover:text-white transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => setShowUserDropdown(false)}
                    >
                      📁 Import Data
                    </Link>
                    <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <button
                        onClick={onLogout}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700 hover:text-red-300 transition-colors"
                        style={{ color: 'var(--danger-color)' }}
                      >
                        🚪 Sign Out
                      </button>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TestNavigation;
